import clsx from "clsx";
import {
  type ComponentProps,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Icon } from "@shared/ui/icon";
import { Textarea } from "@shared/ui/textarea/textarea";
import { Body } from "@shared/ui/typography";

import type { RatingType } from "@/_pages/ai-assistant/model";

import s from "./feedback.module.scss";

const MAX_LENGHT_INPUT = 500;

export type CompProps = ComponentProps<"div"> & {
  className?: string;
  scrollToBottom: (sectionId: string | null) => void;
  onHideFeedback: () => void;
  onSetRating: (data: RatingType, dialogue_id: string) => void;
  dialogueId: string | null;
};

export const Feedback = (props: CompProps) => {
  const { className, scrollToBottom, onSetRating, onHideFeedback, dialogueId } =
    props;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [feedbackData, setFeedbackData] = useState({
    comment: "",
    rating: 0,
  });

  const [isSuccess, setIsSuccess] = useState(false);

  const handleClickOnStar = useCallback((idx: number) => {
    setFeedbackData((prev) => {
      return { ...prev, rating: idx };
    });
    scrollToBottom(null);

    timeoutRef.current = setTimeout(() => {
      scrollToBottom(null);

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutHideRef.current !== null) {
        clearTimeout(timeoutHideRef.current);
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = useCallback(() => {
    if (dialogueId) {
      onSetRating(feedbackData, dialogueId);
      setIsSuccess(true);
      setFeedbackData({
        comment: "",
        rating: 0,
      });

      timeoutHideRef.current = setTimeout(() => {
        onHideFeedback();
        if (timeoutHideRef.current !== null) {
          clearTimeout(timeoutHideRef.current);
        }
      }, 5000);
    }
  }, [feedbackData, dialogueId]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFeedbackData((prev) => {
        return {
          ...prev,
          comment:
            e.target.value.length <= MAX_LENGHT_INPUT
              ? e.target.value
              : prev.comment,
        };
      });
    },
    [],
  );

  return (
    <div
      className={clsx(s.root, className, {
        [s.viewMessage]: feedbackData.rating,
        [s.succses]: isSuccess,
      })}
    >
      <div className={s.wrapper}>
        {isSuccess ? (
          <Body weight="semiBold">Спасибо за отзыв!</Body>
        ) : (
          <>
            <div className={s.main}>
              <div className={s.section}>
                <Icon name="sur" />
                <Body weight="semiBold">Оцените ответ AI-помощника</Body>
              </div>
              <div className={s.rating}>
                {Array.from({ length: 5 }).map((_, idx) => {
                  const value = 5 - idx;

                  return (
                    <Fragment key={`rating-${value}`}>
                      <input
                        type="radio"
                        id={`rating-${value}`}
                        name="rating"
                        value={value}
                        className={s.starInput}
                        onChange={() => handleClickOnStar(value)}
                      />
                      <label htmlFor={`rating-${value}`} className={s.star}>
                        <Icon name="star" />
                      </label>
                    </Fragment>
                  );
                })}
              </div>
            </div>
            <div className={s.messageWrapper}>
              <div className={s.messageContent}>
                <Textarea
                  id="ai-comment"
                  placeholder="Добавьте комментарий"
                  commentLength={feedbackData.comment.length}
                  maxLengthUI={MAX_LENGHT_INPUT}
                  className={s.textarea}
                  onChange={handleChange}
                />

                <ButtonWithIcon
                  iconName="arrow"
                  iconFillColor="cadmium"
                  variant="secondary-dark"
                  onClick={handleSubmit}
                >
                  отправить
                </ButtonWithIcon>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

Feedback.displayName = "Feedback";
